/**
 * The drawer header's Refresh button — available on EVERY tab.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Replaces the old header refresh, which only existed while the Standard tab
 * was open and was hardcoded to say "Voluum". The point of the button is to
 * avoid closing the drawer and reopening it through Edit FO just to see whether
 * an element is still live.
 */

import type { FC } from 'react';
import { RefreshCw } from 'lucide-react';
import { trackerLabel, type FlowOptimizerConfig } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import type { FlowOptimizerProvider } from '../services/flow-optimizer-api';
import RefreshTooltip from './RefreshTooltip';
import { useDrawerRefresh } from './useDrawerRefresh';

interface Props {
  campaignId: string;
  provider: FlowOptimizerProvider;
  config: FlowOptimizerConfig;
}

export const RefreshDrawerButton: FC<Props> = ({
  campaignId,
  provider,
  config,
}) => {
  const state = useDrawerRefresh({ campaignId, provider });
  return (
    <Tooltip
      side="bottom"
      interactive
      contentClassName="max-w-sm"
      content={
        <RefreshTooltip
          config={config}
          provider={provider}
          refreshesWeights={state.refreshesWeights}
          refreshesFlow={state.refreshesFlow}
          flowHeldByDirty={state.flowHeldByDirty}
        />
      }
    >
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => void state.refresh()}
        loading={state.refreshing}
        className="h-7 shrink-0 gap-1 px-2"
        aria-label={`Refresh from ${trackerLabel(provider)}`}
      >
        <RefreshCw
          className={cn('h-3 w-3', state.refreshing && 'animate-spin')}
          aria-hidden
        />
        <span className="hidden sm:inline">Refresh</span>
      </Button>
    </Tooltip>
  );
};

export default RefreshDrawerButton;
